/**
 * Option Indicator service — talks to /api/v1/option-indicator/* (see
 * app.api.v1.option_indicator). Sibling to optionsDashboardService/
 * indiaVixService, not a replacement for either.
 */
import api from "@/services/api";
import type {
  OIScoreSnapshot,
  OptionIndicatorAlertsRead,
  OptionIndicatorDashboardRead,
  OptionIndicatorRunNowResponse,
} from "@/types";

export const optionIndicatorService = {
  async getDashboard(symbol: string): Promise<OptionIndicatorDashboardRead> {
    const { data } = await api.get<OptionIndicatorDashboardRead>(`/option-indicator/${symbol}/dashboard`);
    return data;
  },
  async getAlerts(symbol: string, limit = 20): Promise<OptionIndicatorAlertsRead> {
    const { data } = await api.get<OptionIndicatorAlertsRead>(`/option-indicator/${symbol}/alerts`, {
      params: { limit },
    });
    return data;
  },
  /** Admin-only (backend enforces require_roles("ADMIN")). */
  async runNow(): Promise<OptionIndicatorRunNowResponse> {
    const { data } = await api.post<OptionIndicatorRunNowResponse>("/option-indicator/run-now");
    return data;
  },
  /**
   * Composite OI Score v2 (regime / G3 gate / decision / confirmation) for
   * this symbol — read from the SIBLING `/oi-score` API (Modules 5 & 6,
   * `app.api.v1.oi_score`), not a new Option Indicator endpoint. This data
   * is already computed and persisted by the existing scoring engine
   * (`app.services.oi_scoring.engine.compute_oi_score`); the Option
   * Indicator page surfaces it alongside its own Wave 2a analytics rather
   * than recomputing a duplicate copy.
   */
  async getCompositeScore(symbol: string): Promise<OIScoreSnapshot> {
    const { data } = await api.get<OIScoreSnapshot>(`/oi-score/${symbol}/latest`);
    return data;
  },
};

export default optionIndicatorService;
